import React, { useState, useEffect } from 'react';
import { Button } from '../layout/buttons';
import { Modal, Row, Col } from 'react-bootstrap';
import { PageHeader } from '../layout';
import SimpleTable from '../layout/table/SimpleTable';
import { fetchUserCompanies, fetchCompanies, createCompany, updateCompany, deleteCompany, joinCompany, fetchJoinRequests, approveJoinRequest, rejectJoinRequest } from '../../utils/companies';
import { logger } from '../../utils/logger';
import styles from './Profile.module.css';

const CompanyManagement = ({ userData, onCompanyUpdate }) => {
  const [companies, setCompanies] = useState([]);
  const [userCompany, setUserCompany] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [joinableCompanies, setJoinableCompanies] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  
  // Form state for creating company
  const [companyData, setCompanyData] = useState({
    name: '',
    url: '',
    objective: '',
    industry: '',
    isPublic: false,
    metadata: {
      size: '',
      location: '',
      sector: ''
    }
  });

  // Join company form state
  const [joinData, setJoinData] = useState({
    companyId: '',
    role: 'employee'
  });

  useEffect(() => {
    if (userData?.id) {
      loadUserCompanies();
    }
  }, [userData]);

  const loadUserCompanies = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch companies where user is owner or employee using the new API
      const userCompaniesResult = await fetchUserCompanies();
      
      if (userCompaniesResult.success && userCompaniesResult.companies) {
        const userCompanies = userCompaniesResult.companies;
        const myCompany = userCompanies.length > 0 ? userCompanies[0] : null;

        setUserCompany(myCompany);
        setCompanies(userCompanies);
        loadJoinRequests(userCompanies);
      } else {
        setUserCompany(null);
        setCompanies([]);
        setJoinRequests([]);
      }
    } catch (err) {
      logger.error('Error loading user companies:', err);
      setError('Failed to load companies');
      setUserCompany(null);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const loadJoinableCompanies = async () => {
    try {
      setError('');
      const result = await fetchCompanies({ isPublic: true, pageSize: 100 });
      if (result.success && result.companies) {
        const joinedIds = new Set(companies.map(c => c.id));
        setJoinableCompanies(result.companies.filter(c => !joinedIds.has(c.id)));
      } else {
        setJoinableCompanies([]);
      }
    } catch (err) {
      logger.error('Error loading joinable companies:', err);
      setJoinableCompanies([]);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      // Validate required fields
      if (!companyData.name.trim()) {
        setError('Company name is required');
        return;
      }

      let result;
      if (userCompany) {
        // Update existing company
        result = await updateCompany(userCompany.id, companyData);
      } else {
        // Create new company
        result = await createCompany(companyData);
      }
      
      if (result.success) {
        setShowCreateModal(false);
        await loadUserCompanies(); // Refresh the list
        onCompanyUpdate(result.company);
        
        // Reset form
        setCompanyData({
          name: '',
          url: '',
          objective: '',
          industry: '',
          isPublic: false,
          metadata: {
            size: '',
            location: '',
            sector: ''
          }
        });
        setUserCompany(null); // Clear editing state
      } else {
        setError(result.message || `Failed to ${userCompany ? 'update' : 'create'} company`);
      }
    } catch (err) {
      logger.error(`Error ${userCompany ? 'updating' : 'creating'} company:`, err);
      setError(`Failed to ${userCompany ? 'update' : 'create'} company`);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinCompany = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      if (!joinData.companyId) {
        setError('Please select a company to join');
        return;
      }

      const result = await joinCompany(joinData.companyId, joinData.role);
      
      if (result.success) {
        setShowJoinModal(false);
        setError('');
        setSuccessMessage(result.pendingApproval
          ? 'Join request sent. The organization owner must approve it before you get access.'
          : 'You joined the organization successfully.');
        
        // Reset form
        setJoinData({ companyId: '', role: 'employee' });
        
        if (!result.pendingApproval) {
          setUserCompany(result.company);
          onCompanyUpdate(result.company);
          await loadUserCompanies(); // Refresh the list
        }
      } else {
        setError(result.message || 'Failed to join company');
      }
    } catch (err) {
      logger.error('Error joining company:', err);
      setError('Failed to join company');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCompany = (company) => {
    // Populate form with company data for editing
    setCompanyData({
      name: company.name || '',
      url: company.url || '',
      objective: company.objective || '',
      industry: company.industry || '',
      isPublic: company.isPublic || false,
      metadata: {
        size: company.metadata?.size || '',
        location: company.metadata?.location || '',
        sector: company.metadata?.sector || ''
      }
    });
    setUserCompany(company); // Set current company for editing
    setShowCreateModal(true); // Reuse create modal for editing
  };

  const handleDeleteCompany = async (company) => {
    if (!window.confirm(`Are you sure you want to delete "${company.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const result = await deleteCompany(company.id);
      
      if (result.success) {
        await loadUserCompanies(); // Refresh the list
        if (onCompanyUpdate) {
          onCompanyUpdate(null);
        }
      } else {
        setError(result.message || 'Failed to delete company');
      }
    } catch (err) {
      logger.error('Error deleting company:', err);
      setError('Failed to delete company');
    } finally {
      setLoading(false);
    }
  };

  const loadJoinRequests = async (companyList = companies) => {
    try {
      setError('');
      const ownedCompanies = companyList.filter((c) => c.ownerId === userData?.id);
      if (ownedCompanies.length === 0) {
        setJoinRequests([]);
        return;
      }
      const requests = [];
      for (const company of ownedCompanies) {
        const result = await fetchJoinRequests(company.id);
        if (result.success && result.requests) {
          requests.push(...result.requests.map((r) => ({ ...r, companyId: company.id, companyName: company.name })));
        }
      }
      setJoinRequests(requests);
    } catch (err) {
      logger.error('Error loading join requests:', err);
      setJoinRequests([]);
    }
  };

  const handleApproveRequest = async (companyId, userId) => {
    try {
      setLoading(true);
      setError('');
      const result = await approveJoinRequest(companyId, userId);
      if (result.success) {
        setSuccessMessage(result.message || 'Join request approved');
        await loadJoinRequests();
        await loadUserCompanies(); // Refresh so the new employee appears in the list
      } else {
        setError(result.message || 'Failed to approve join request');
      }
    } catch (err) {
      logger.error('Error approving join request:', err);
      setError('Failed to approve join request');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectRequest = async (companyId, userId) => {
    try {
      setLoading(true);
      setError('');
      const result = await rejectJoinRequest(companyId, userId);
      if (result.success) {
        setSuccessMessage(result.message || 'Join request rejected');
        await loadJoinRequests();
      } else {
        setError(result.message || 'Failed to reject join request');
      }
    } catch (err) {
      logger.error('Error rejecting join request:', err);
      setError('Failed to reject join request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.companyManagement}>
      {companies.length > 0 ? (
        <div className={styles.dataContainerCard}>
          <PageHeader 
            title="Your Organizations" 
            subtext="Organizations you own or are an employee of"
          />
          
          {error && <div className={styles.errorMessage}>{error}</div>}
          {successMessage && <div className={styles.successMessage}>{successMessage}</div>}
          
          <SimpleTable
            headers={['Organization Name', 'Industry', 'Role', 'Visibility', 'Actions']}
            className={styles.companiesTable}
          >
            {companies.map((company) => (
              <tr key={company.id}>
                <td>
                  <div className={styles.companyNameCell}>
                    <strong>{company.name}</strong>
                    {company.url && (
                      <div className={styles.companyUrl}>
                        <a href={company.url} target="_blank" rel="noopener noreferrer">
                          {company.url}
                        </a>
                      </div>
                    )}
                  </div>
                </td>
                <td>{company.industry || 'Not specified'}</td>
                <td>
                  <span className={`${styles.roleBadge} ${company.ownerId === userData?.id ? styles.ownerRole : styles.employeeRole}`}>
                    {company.ownerId === userData?.id ? 'Owner' : 'Employee'}
                  </span>
                </td>
                <td>
                  <span className={`${styles.visibilityBadge} ${company.isPublic ? styles.publicVisibility : styles.privateVisibility}`}>
                    {company.isPublic ? 'Public' : 'Private'}
                  </span>
                </td>
                <td>
                  <div className={styles.actionButtons}>
                    <Button 
                      variant="outline-neutral" 
                      size="sm"
                      onClick={() => handleEditCompany(company)}
                    >
                      Edit
                    </Button>
                    {company.ownerId === userData?.id && (
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleDeleteCompany(company)}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </SimpleTable>

          {joinRequests.length > 0 && (
            <div className={styles.joinRequestsCard}>
              <h4>Pending Join Requests</h4>
              <p className={styles.joinRequestsSubtext}>
                Approve requests to grant employees access to your organizations.
              </p>
              <SimpleTable
                headers={['Organization', 'User', 'Requested Role', 'Requested', 'Actions']}
                className={styles.companiesTable}
              >
                {joinRequests.map((req) => (
                  <tr key={`${req.companyId}-${req.userId}`}>
                    <td><strong>{req.companyName}</strong></td>
                    <td>{req.displayName || req.userId}</td>
                    <td>{req.role || 'employee'}</td>
                    <td>{req.requestedAt ? new Date(req.requestedAt).toLocaleDateString() : '—'}</td>
                    <td>
                      <div className={styles.actionButtons}>
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={loading}
                          onClick={() => handleApproveRequest(req.companyId, req.userId)}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          disabled={loading}
                          onClick={() => handleRejectRequest(req.companyId, req.userId)}
                        >
                          Reject
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </SimpleTable>
            </div>
          )}

          <div className={styles.companyActions}>
            <Button 
              variant="primary" 
              onClick={() => {
                setUserCompany(null); // Clear editing state
                setCompanyData({ // Reset form to clean state
                  name: '',
                  url: '',
                  objective: '',
                  industry: '',
                  isPublic: false,
                  metadata: {
                    size: '',
                    location: '',
                    sector: ''
                  }
                });
                setShowCreateModal(true);
              }}
              className={styles.actionButton}
            >
              Create New Organization
            </Button>
            <Button 
              variant="outline-neutral" 
              onClick={() => {
                loadJoinableCompanies();
                setShowJoinModal(true);
              }}
              className={styles.actionButton}
            >
              Join Existing Organization
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.dataContainerCard}>
          <PageHeader 
            title="Organization Setup" 
            subtext="Create or join an Organization to access stakeholder management features"
          />
          
          {error && <div className={styles.errorMessage}>{error}</div>}
          {successMessage && <div className={styles.successMessage}>{successMessage}</div>}
          
          <div className={styles.companyActions}>
            <Button 
              variant="primary" 
              onClick={() => {
                setUserCompany(null); // Clear editing state
                setCompanyData({ // Reset form to clean state
                  name: '',
                  url: '',
                  objective: '',
                  industry: '',
                  isPublic: false,
                  metadata: {
                    size: '',
                    location: '',
                    sector: ''
                  }
                });
                setShowCreateModal(true);
              }}
              className={styles.actionButton}
            >
              Create New Organization
            </Button>
            <Button 
              variant="outline-neutral" 
              onClick={() => {
                loadJoinableCompanies();
                setShowJoinModal(true);
              }}
              className={styles.actionButton}
            >
              Join Existing Organization
            </Button>
          </div>
        </div>
      )}

      {/* Create Company Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{userCompany ? 'Edit Organization' : 'Create New Organization'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <div className={styles.errorMessage}>{error}</div>}
          
          <form onSubmit={handleCreateCompany}>
            <Row>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label>Organization Name *</label>
                  <input
                    type="text"
                    value={companyData.name}
                    onChange={(e) => setCompanyData(prev => ({ ...prev, name: e.target.value }))}
                    className={styles.formInput}
                    required
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label>Website</label>
                  <input
                    type="url"
                    value={companyData.url}
                    onChange={(e) => setCompanyData(prev => ({ ...prev, url: e.target.value }))}
                    className={styles.formInput}
                    placeholder="https://example.com"
                  />
                </div>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label>Industry</label>
                  <input
                    type="text"
                    value={companyData.industry}
                    onChange={(e) => setCompanyData(prev => ({ ...prev, industry: e.target.value }))}
                    className={styles.formInput}
                    placeholder="e.g., Technology, Healthcare, Finance"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label>Organization Size</label>
                  <select
                    value={companyData.metadata.size}
                    onChange={(e) => setCompanyData(prev => ({ 
                      ...prev, 
                      metadata: { ...prev.metadata, size: e.target.value }
                    }))}
                    className={styles.formInput}
                  >
                    <option value="">Select size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="501-1000">501-1000 employees</option>
                    <option value="1000+">1000+ employees</option>
                  </select>
                </div>
              </Col>
            </Row>

            <div className={styles.formGroup}>
              <label>Location</label>
              <input
                type="text"
                value={companyData.metadata.location}
                onChange={(e) => setCompanyData(prev => ({ 
                  ...prev, 
                  metadata: { ...prev.metadata, location: e.target.value }
                }))}
                className={styles.formInput}
                placeholder="e.g., San Francisco, CA"
              />
            </div>

            <div className={styles.formGroup}>
              <label>Organization Objective</label>
              <textarea
                value={companyData.objective}
                onChange={(e) => setCompanyData(prev => ({ ...prev, objective: e.target.value }))}
                className={styles.formTextarea}
                placeholder="What is your organization's main objective or mission?"
                rows={3}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={companyData.isPublic}
                  onChange={(e) => setCompanyData(prev => ({ ...prev, isPublic: e.target.checked }))}
                  className={styles.checkbox}
                />
                Make this organization publicly discoverable
              </label>
            </div>

            <div className={styles.modalActions}>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading
                  ? (userCompany ? 'Editing...' : 'Creating...')
                  : (userCompany ? 'Edit Company' : 'Create Organization')}
              </Button>
              <Button 
                variant="outline-neutral" 
                type="button"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/* Join Company Modal */}
      <Modal show={showJoinModal} onHide={() => setShowJoinModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Join Organization</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <div className={styles.errorMessage}>{error}</div>}
          
          <form onSubmit={handleJoinCompany}>
            <div className={styles.formGroup}>
              <label>Select Organization</label>
              <select
                value={joinData.companyId}
                onChange={(e) => setJoinData(prev => ({ ...prev, companyId: e.target.value }))}
                className={styles.formInput}
                required
              >
                <option value="">Choose a organization...</option>
                {joinableCompanies.length > 0 ? (
                  joinableCompanies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name} {company.isPublic ? '(Public)' : '(Private)'}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>No organizations available to join</option>
                )}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Role</label>
              <select
                value={joinData.role}
                onChange={(e) => setJoinData(prev => ({ ...prev, role: e.target.value }))}
                className={styles.formInput}
              >
                <option value="employee">Employee</option>
              </select>
            </div>

            <div className={styles.modalActions}>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? 'Joining...' : 'Join Company'}
              </Button>
              <Button 
                variant="outline-neutral" 
                type="button"
                onClick={() => setShowJoinModal(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CompanyManagement;
